import React, { createContext, useContext, useState, useEffect, ReactNode, useMemo, useCallback, useRef } from 'react';
import { Control, TenantConfig } from '../type';
import { EstimateResponse } from '../types';

// Define API_BASE_URL locally
const API_BASE_URL = "https://netpriceestimate.com";


// Define the shape of your context
export interface TenantContextType {
    tenantConfig: TenantConfig | null;
    entitiesData: Control[] | null;
    derivedAttributesData: EstimateResponse | null;
    authorityId: string | null;
    loading: boolean;
    estimateBody: EstimateResponse | null;
    estimateId: string | null;
    displayName: string | null;
    logoUrl: string | null;
    primaryColor: string | null;
    getEntityIdByName: (name: string) => string | undefined;
    getAttributesByEntityName: (entityName: string) => any[];
    getFAQAttributesByEntityId: (entityId: string) => any[];
    getWelcomeFAQAttributes: () => any[];
    handleNext: (entityName: string, updatedAttributes: { name: string; value: any }[]) => Promise<void>;
    calculateDerivedAttributesForEntity: (entityId: string, overrideBody?: any, currentEntityId?: string) => Promise<EstimateResponse>;
    getTenantName: () => string;
}

export const TenantContext = createContext<TenantContextType | undefined>(undefined);

interface TenantProviderProps {
    children: ReactNode;
}

export const TenantProvider: React.FC<TenantProviderProps> = ({ children }) => {
    const [tenantConfig, setTenantConfig] = useState<TenantConfig | null>(null);
    const [entitiesData, setEntitiesData] = useState<Control[] | null>(null);
    const [derivedAttributesData, setDerivedAttributesData] = useState<EstimateResponse | null>(null);
    const [authorityId, setAuthorityId] = useState<string | null>(null);
    const [estimateId, setEstimateId] = useState<string | null>(null);
    const [loading, setLoading] = useState<boolean>(true);
    const [estimateBody, setEstimateBody] = useState<EstimateResponse | null>(null);
    const [displayName, setDisplayName] = useState<string | null>(null);
    const [logoUrl, setLogoUrl] = useState<string | null>(null);
    const [primaryColor, setPrimaryColor] = useState<string | null>(null);

    // Track in-flight entity calculations to avoid duplicate concurrent calls
    const inFlightEntitiesRef = useRef<Set<string>>(new Set());
    // Refs to keep latest values for stable callbacks and to avoid effect dependency loops
    const authorityIdRef = useRef<string | null>(authorityId);
    const estimateIdRef = useRef<string | null>(estimateId);
    const estimateBodyRef = useRef<EstimateResponse | null>(estimateBody);
    const derivedAttributesDataRef = useRef<EstimateResponse | null>(derivedAttributesData);

    // Small helpers used by the calculate function to keep the main function simpler
    const getCurrentEstimateId = (overrideBody?: any) => {
        return estimateIdRef.current ?? overrideBody?._id ?? estimateBodyRef.current?._id;
    };

    const getObjectsFrom = (overrideBody?: any) => {
        return overrideBody?.objects ?? estimateBodyRef.current?.objects ?? [];
    };

    // Helper function to get tenant name from localStorage
    const getTenantName = useCallback((): string => {
        try {
            const tenantName = localStorage.getItem('TENANT_NAME');
            if (!tenantName) {
                console.warn('TENANT_NAME not found in localStorage');
                return '';
            }
            return tenantName;
        } catch (error) {
            console.error('Failed to retrieve TENANT_NAME from localStorage:', error);
            return '';
        }
    }, []);

    // Utility function to normalize authority code to lowercase
    const normalizeAuthorityCode = useCallback((authorityCode: string): string => {
        if (!authorityCode) {
            console.warn('Authority code is empty or undefined');
            return '';
        }
        return authorityCode.trim().toLowerCase();
    }, []);

    const preparePayload = (overrideBody?: any) => {
        return structuredClone(overrideBody ?? estimateBodyRef.current);
    };

    const prepareHeaders = (entityId: string) => {
        const tenantName = getTenantName();
        const normalizedTenantName = normalizeAuthorityCode(tenantName);
        const headers: Record<string, string> = {
            'Content-Type': 'application/json',
            'authority-name': normalizedTenantName,
            'entity-id': entityId,
        };
        if (authorityIdRef.current) headers['AuthorityId'] = authorityIdRef.current;
        return headers;
    };

    const doCalculateRequest = async (url: string, headers: Record<string, string>, payload: any, currentEntityId?: string) => {
        const requestHeaders = {
            ...headers,
            ...(currentEntityId && { 'X-Current-Entity-Id': currentEntityId }),
        };
        const response = await fetch(url, {
            method: 'POST',
            headers: requestHeaders,
            body: JSON.stringify(payload),
        });

        if (!response.ok) {
            const contentType = response.headers.get('content-type') ?? '';
            const bodyText = contentType.includes('application/json')
                ? JSON.stringify(await response.json().catch(() => ({})))
                : await response.text().catch(() => '');
            throw new Error(`calculate-derived-attributes failed ${response.status}: ${bodyText}`);
        }

        return response.json();
    };

    const callEstimatesApi = useCallback(async (authorityCode: string, resumeToken: string, authorityId: string | null) => {
        const response = await fetch(`${API_BASE_URL}/api/estimates`, {
            method: 'POST',
            headers: {
                'Content-Type': 'application/json',
                'resumeToken': resumeToken,
                'authority-name': authorityCode.toLowerCase(),
            },
            body: JSON.stringify({ authorityCode: authorityCode.toLowerCase(), authorityId }),
        });

        if (!response.ok) {
            const text = await response.text().catch(() => '');
            throw new Error(`Estimates API HTTP error ${response.status}: ${text}`);
        }

        return response.json();
    }, []);

    const fetchAuthorityData = useCallback(async (tenantName: string) => {
        const normalizedTenantName = tenantName.trim().toLowerCase();
        const apiResponse = await fetch(`${API_BASE_URL}/api/authorities/${normalizedTenantName}`, {
            method: 'GET',
            headers: {
                'authority-code': normalizedTenantName,
                'authority-name': normalizedTenantName
            }
        });
        if (!apiResponse.ok) {
            throw new Error(`Authority API error ${apiResponse.status}`);
        }
        const responseData = await apiResponse.json();
        const CLIENTGTM_ID = responseData?.authority?.authorityBranding?.googleTagManagerId ?? null;
        localStorage.setItem('CLIENTGTMID', CLIENTGTM_ID);
        const headerLinkString = responseData?.authority?.authorityBranding?.headerLink ?? '[]';
        const fullname = responseData?.authority?.displayName ?? '';
        localStorage.setItem('TENANT_FULLNAME', fullname);
        localStorage.setItem('TENANT_NAME', tenantName);
        const logo = responseData?.authority?.authorityBranding?.logoUrl ?? '';
        localStorage.setItem('TENANT_LOGO', logo);
        const primaryColorValue = responseData?.authority?.authorityBranding?.primaryColor ?? '';
        localStorage.setItem('PRIMARY_COLOR', primaryColorValue);
        const footerLinkString = responseData?.authority?.authorityBranding?.footerlink ?? '[]';
        localStorage.setItem('FOOTER_LINKS', footerLinkString);

        // Set state variables for displayName, logoUrl, and primaryColor
        setDisplayName(fullname);
        setLogoUrl(logo);
        setPrimaryColor(primaryColorValue);

        // Define the proper type for headerLinks
        interface HeaderLink {
            key: string;
            value: string;
        }

        let headerLinks: HeaderLink[] = [];

        try {
            // Parse the JSON string to get the array
            const parsed = JSON.parse(headerLinkString);
            headerLinks = Array.isArray(parsed) ? parsed : [];
        } catch (error) {
            console.error('Error parsing headerLink JSON:', error);
            headerLinks = [];
        }

        // Method 1: Find specific values by key
        const getValueByKey = (key: string): string | null => {
            const link = headerLinks.find((link: HeaderLink) => link.key === key);
            return link?.value ?? null;
        };

        // Extract specific values
        const resourcesUrl = getValueByKey('Resources');
        const aboutUrl = getValueByKey('About');
        const contactUrl = getValueByKey('Contact');

        // Method 2: Store individual values in localStorage
        localStorage.setItem('RESOURCES_URL', resourcesUrl ?? '');
        localStorage.setItem('ABOUT_URL', aboutUrl ?? '');
        localStorage.setItem('CONTACT_URL', contactUrl ?? '');

        // Method 3: Store as a map for easier access
        const headerLinksMap = headerLinks.reduce((acc: Record<string, string>, link: HeaderLink) => {
            acc[link.key] = link.value;
            return acc;
        }, {});

        localStorage.setItem('HEADER_LINKS_MAP', JSON.stringify(headerLinksMap));

        // Method 4: Store the entire parsed array
        localStorage.setItem('HEADER_LINKS_ARRAY', JSON.stringify(headerLinks));

        // Extract and process footer links similarly
        // Re-extract footerLinkString for footer processing
        const footerLinkStringForProcessing = responseData?.authority?.authorityBranding?.footerlink ?? '[]';

        // Define the proper type for footerLinks
        interface FooterLink {
            key: string;
            value: string;
        }

        let footerLinks: FooterLink[] = [];

        try {
            // Parse the JSON string to get the array
            const parsedFooter = JSON.parse(footerLinkStringForProcessing);
            footerLinks = Array.isArray(parsedFooter) ? parsedFooter : [];
        } catch (error) {
            console.error('Error parsing footerLink JSON:', error);
            footerLinks = [];
        }

        // Method 1: Find specific footer values by key
        const getFooterValueByKey = (key: string): string | null => {
            const link = footerLinks.find((link: FooterLink) => link.key === key);
            return link?.value ?? null;
        };

        // Extract specific footer values
        const privacyPolicyUrl = getFooterValueByKey('Privacy Policy');
        const termsConditionsUrl = getFooterValueByKey('Terms & Conditions');
        const accessibilityUrl = getFooterValueByKey('Accessibility');

        // Method 2: Store individual footer values in localStorage
        localStorage.setItem('PRIVACY_POLICY_URL', privacyPolicyUrl ?? '');
        localStorage.setItem('TERMS_CONDITIONS_URL', termsConditionsUrl ?? '');
        localStorage.setItem('ACCESSIBILITY_URL', accessibilityUrl ?? '');

        // Method 3: Store footer links as a map for easier access
        const footerLinksMap = footerLinks.reduce((acc: Record<string, string>, link: FooterLink) => {
            acc[link.key] = link.value;
            return acc;
        }, {});

        localStorage.setItem('FOOTER_LINKS_MAP', JSON.stringify(footerLinksMap));

        // Method 4: Store the entire parsed footer array
        localStorage.setItem('FOOTER_LINKS_ARRAY', JSON.stringify(footerLinks));

        return responseData;
    }, []);
    // Calculate derived attributes for a specific entity dynamically
    // Make this callback identity stable by not depending on state variables directly.
    // It will read latest values from refs which are kept in sync below.
    const calculateDerivedAttributesForEntity = useCallback(async (entityId: string, overrideBody?: any, currentEntityId?: string): Promise<EstimateResponse> => {
        if (!entityId) {
            throw new Error('calculateDerivedAttributesForEntity: entityId is required');
        }

        // Prevent duplicate concurrent execution for same entity
        if (inFlightEntitiesRef.current.has(entityId)) {
            console.warn(`Calculate already in progress for entity: ${entityId}`);
            const existing = derivedAttributesDataRef.current;
            if (existing) return existing;
            throw new Error(`In-flight request for ${entityId} but no existing data to return.`);
        }

        inFlightEntitiesRef.current.add(entityId);

        try {
            const currentEstimateId = getCurrentEstimateId(overrideBody);
            if (!currentEstimateId) throw new Error('Cannot calculate derived attributes: estimateId not available');

            const objects: any[] = getObjectsFrom(overrideBody);
            if (!Array.isArray(objects) || objects.length === 0) throw new Error('Cannot calculate derived attributes: no objects available in payload');

            const firstObject = objects[0];
            if (!firstObject?._id) throw new Error('Cannot calculate derived attributes: no valid object found');

            const objectId = firstObject._id;
            const url = `${API_BASE_URL}/api/estimates/${currentEstimateId}/objects/${objectId}/calculate-derived-attributes`;

            // Prepare payload (deep copy)
            const payload = preparePayload(overrideBody);
            if (!payload || !Array.isArray(payload.objects)) throw new Error('Payload for calculate-derived-attributes must include objects array');

            const headers = prepareHeaders(entityId);

            const json = await doCalculateRequest(url, headers, payload, currentEntityId);

            // Update context state with server response and sync refs
            setDerivedAttributesData(json);
            derivedAttributesDataRef.current = json;

            if (json?.objects) {
                setEstimateBody({ ...json });
                estimateBodyRef.current = { ...json };
                // If the estimate id wasn't set before, ensure estimateId state/ref are set
                if (!estimateIdRef.current && json._id) {
                    setEstimateId(json._id);
                    estimateIdRef.current = json._id;
                }
            }

            return json;
        } catch (err) {
            console.error('calculateDerivedAttributesForEntity error:', err);
            throw err;
        } finally {
            inFlightEntitiesRef.current.delete(entityId);
        }
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    // Keep refs in sync whenever state changes
    useEffect(() => { authorityIdRef.current = authorityId; }, [authorityId]);
    useEffect(() => { estimateIdRef.current = estimateId; }, [estimateId]);
    useEffect(() => { estimateBodyRef.current = estimateBody; }, [estimateBody]);
    useEffect(() => { derivedAttributesDataRef.current = derivedAttributesData; }, [derivedAttributesData]);

    useEffect(() => {
        const url = globalThis.location.pathname;
        const tenantName = url.split('/')[1];
        // Persist the last known tenant path segment so other components can navigate back to it
        try { if (tenantName) localStorage.setItem('LAST_TENANT', tenantName); } catch (e) { console.error('Failed to persist tenant to localStorage:', e); }

        const loadTenantAndAuthorityData = async () => {
            try {
                setLoading(true);

                // Fetch authority data
                const authorityData = await fetchAuthorityData(tenantName);
                setTenantConfig(authorityData);
                const authId: string | null = authorityData?.authority?.authorityId ?? null;
                setAuthorityId(authId);

                // Call estimates API to get Welcome data and full estimate
                // Use tenantName (from first API request headers) for consistency
                const estimatesResponse = await callEstimatesApi(tenantName, '20251110', authId);

                // Extract entities from applicableEntities
                const entitiesFromResponse = estimatesResponse.applicableEntities ?? [];
                setEntitiesData(entitiesFromResponse);

                // Set estimateId from response
                setEstimateId(estimatesResponse._id);

                // Set derivedAttributesData to the full response
                setDerivedAttributesData(estimatesResponse);

                // Set estimateBody from the response
                setEstimateBody(estimatesResponse);

                // Additional call to calculate-derived-attributes for the Estimate entity
                // This is the second call as requested, using the estimateId as entityId
                if (estimatesResponse._id) {
                    try {
                        await calculateDerivedAttributesForEntity(estimatesResponse._id);
                    } catch (err) {
                        console.error('Failed to calculate derived attributes for Estimate entity on initial load:', err);
                        // Optionally handle the error, e.g., show a notification or retry
                    }
                }

            } catch (err) {
                console.error(`Failed to load configuration for tenant: ${tenantName}`, err);
                setTenantConfig(null);
                setEntitiesData(null);
                setDerivedAttributesData(null);
                setAuthorityId(null);
                setEstimateId(null);
            } finally {
                setLoading(false);
            }
        };

        loadTenantAndAuthorityData();
    }, [callEstimatesApi, fetchAuthorityData, calculateDerivedAttributesForEntity]);

    const handleNext = useCallback(async (entityName: string, updatedAttributes: { name: string; value: any }[]) => {
        if (!authorityId || !estimateBody) {
            console.error("Cannot call handleNext, authorityId or estimateBody is not set.");
            return;
        }

        // Create a deep copy of the current estimateBody
        const newEstimateBody = structuredClone(estimateBody);
        const objectToUpdate = newEstimateBody.objects?.find((obj: any) =>
            obj.name === entityName || obj.entityId === (entitiesData?.find(e => e.name === entityName)?._id)
        );

        if (objectToUpdate) {
            for (const updatedAttr of updatedAttributes) {
                const attr = objectToUpdate.attributes?.find((a: any) =>
                    a.name === updatedAttr.name || a._id === updatedAttr.name
                );
                if (attr) {
                    attr.value = updatedAttr.value;
                    attr.isNa = false;
                }
            }

            try {
                const entityIdForObject = objectToUpdate.entityId;
                const apiResponse = await calculateDerivedAttributesForEntity(entityIdForObject, newEstimateBody);
                setDerivedAttributesData(apiResponse);
                if (apiResponse.objects) {
                    setEstimateBody(apiResponse);
                }
            } catch (err) {
                console.error('handleNext: calculateDerivedAttributesForEntity failed', err);
                throw err;
            }
        } else {
            console.warn('handleNext: objectToUpdate not found for entityName', entityName);
        }
    }, [authorityId, estimateBody, calculateDerivedAttributesForEntity, entitiesData]);

    const getEntityIdByName = useCallback((name: string): string | undefined => {
        if (!entitiesData) return undefined;
        const found = entitiesData.find(e => String(e.name || '').toLowerCase() === String(name || '').toLowerCase());
        return found?._id;
    }, [entitiesData]);

    const getAttributesByEntityName = useCallback((entityName: string): any[] => {
        if (!derivedAttributesData?.objects) return [];

        const entity = derivedAttributesData.objects.find((obj: any) =>
            String(obj.name ?? '').toLowerCase() === String(entityName ?? '').toLowerCase()
        );

        return entity?.attributes ?? [];
    }, [derivedAttributesData]);

    const getFAQAttributesByEntityId = useCallback((entityId: string): any[] => {
        if (!derivedAttributesData?.objects || !Array.isArray(derivedAttributesData.objects)) {
            return [];
        }

        const entity = derivedAttributesData.objects.find((obj: any) => obj.entityId === entityId);

        if (!entity) {
            const faqEntity = derivedAttributesData.objects.find((obj: any) => {
                const name = String(obj?.name ?? '').toLowerCase();
                const desc = String(obj?.description ?? '').toLowerCase();;
                return (name.includes('faq') || desc.includes('faq'));
            });
            return faqEntity?.attributes ?? [];
        }

        return entity?.attributes ?? [];
    }, [derivedAttributesData]);

    const getWelcomeFAQAttributes = useCallback((): any[] => {
        if (!derivedAttributesData?.objects || !Array.isArray(derivedAttributesData.objects)) {
            return [];
        }

        const objects: any[] = derivedAttributesData.objects;

        // Exact match (case-insensitive)
        const exact = objects.find(obj => String(obj.name ?? '').toLowerCase() === 'welcome - faq');
        if (exact && Array.isArray(exact.attributes)) return exact.attributes;

        // Name contains both 'welcome' and 'faq'
        const welcomeFaq = objects.find(obj => {
            const name = String(obj.name ?? '').toLowerCase();
            return name.includes('welcome') && name.includes('faq');
        });
        if (welcomeFaq && Array.isArray(welcomeFaq.attributes)) return welcomeFaq.attributes;

        return [];
    }, [derivedAttributesData]);

    const value = useMemo(() => ({
        tenantConfig,
        entitiesData,
        derivedAttributesData,
        authorityId,
        loading,
        estimateBody,
        estimateId,
        displayName,
        logoUrl,
        primaryColor,
        getEntityIdByName,
        getAttributesByEntityName,
        getFAQAttributesByEntityId,
        getWelcomeFAQAttributes,
        handleNext,
        calculateDerivedAttributesForEntity,
        getTenantName,
    }), [
        tenantConfig,
        entitiesData,
        derivedAttributesData,
        authorityId,
        loading,
        estimateBody,
        estimateId,
        displayName,
        logoUrl,
        primaryColor,
        getEntityIdByName,
        getAttributesByEntityName,
        getFAQAttributesByEntityId,
        getWelcomeFAQAttributes,
        handleNext,
        calculateDerivedAttributesForEntity,
        getTenantName,
    ]);

    return (
        <TenantContext.Provider value={value}>
            {children}
        </TenantContext.Provider>
    );
};

export const useTenant = (): TenantContextType => {
    const context = useContext(TenantContext);
    if (context === undefined) {
        throw new Error('useTenant must be used within a TenantProvider');
    }
    return context;
};